import { createRoute } from '@tanstack/react-router';
import { pharmacyParentRoute } from 'pages/pharmacy/pharmacyParentRoute';
import {
  zodOptionalIdArray,
  zodOptionalString,
  zodPage,
  zodPathParamId,
  zodSort,
} from 'routes/routeUtils';
import { pharmacyTradeEnhancedStatusList, tradesSources } from 'types/api/pharmacies/trades/trade';
import { tradesInstanceStatus } from 'types/models';
import { z } from 'zod';

const pharmacyLabRouteSearchSchema = z.object({
  tab: z
    .enum([
      'home',
      'trades',
      'cases',
      'chat',
      'documents',
      'stock_konnect',
      'lgo_orders',
      'invoices',
      'avoirs',
      'rfa',
    ])
    .default('home'),
  // claims filters
  claim_tab: z.enum(['notClosedByPharmacy', 'archivedByPharmacy']).optional(),
  claim_page: zodPage,
  // trades filters
  trade_date: z.string().optional(),
  trade_query: z.string().optional(),
  trade_priority: z.enum(['all', 'priority']).optional(),
  trade_tagIds: zodOptionalIdArray,
  trade_status: z.array(z.enum([...tradesInstanceStatus])).optional(),
  trade_enhancedStatuses: z.array(z.enum(pharmacyTradeEnhancedStatusList)).optional(),
  trade_source: z.enum([...tradesSources]).optional(),
  documentsPage: z.number().optional(),
  // lgo orders tab
  kopilotOrdersPage: zodPage,
  // invoices sub-tab (infinite scroll → no page param)
  invoicesSort: zodSort([
    'invoice_date',
    'invoice_number',
    'total_amount',
    'claim_amount',
    'supplier',
  ]),
  invoicesQuery: zodOptionalString,
  invoicesDateFrom: z.string().optional(),
  invoicesDateTo: z.string().optional(),
  // avoirs tab
  avoirsQuery: zodOptionalString,
  avoirsDateFrom: z.string().optional(),
  avoirsDateTo: z.string().optional(),
});

export const pharmacyLabRoute = createRoute({
  getParentRoute: () => pharmacyParentRoute,
  path: '/labs/$id',
  params: z.object({ id: zodPathParamId }),
  validateSearch: pharmacyLabRouteSearchSchema,
}).lazy(() => import('pages/pharmacy/Labs/PharmacyLabPage/PharmacyLabPage').then((d) => d.Route));

// Nested invoice-detail route so the lab page remains the rendered parent
// (i.e. stays visible behind the drawer) when opening an invoice from a lab page.
export const pharmacyLabInvoiceDetailRoute = createRoute({
  getParentRoute: () => pharmacyLabRoute,
  path: '/kopilot/invoices/$invoiceId',
  params: z.object({ invoiceId: zodPathParamId }),
}).lazy(() =>
  import('pages/pharmacy/KopilotInvoices/PharmacyKopilotInvoicePanel').then((d) => d.LabRoute)
);
